/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { iconButtonSize } from '../../../../registry/constants';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './IconColumn';
import { helpRenders } from './IconColumn_Help';

const name = 'Icon Column';

const IconColumnManager: ComponentManagerFn<'IconColumn'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'IconColumn'>(platform, 'IconColumn'),
    type: 'IconColumn',
    name,
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'Menu'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        getAttributeDataTypes: ({ props }) => {
          return ['String'];
        },
        required: false,
      },
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      color: {
        label: 'Color',
        type: 'ColorExpression',
        placeholder: 'e.g. primary or #444',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: iconButtonSize,
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
        parentColorProp: 'color',
        useParentColor: true,
      },
      showOnlyOnHover: {
        label: 'Show Only On Hover',
        type: 'boolean',
      },
      commentsItemId: {
        label: 'Comments Item ID',
        type: 'ItemID',
        initialFilter: 'Comments',
      },
      ...toolTipProps,
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }

      switch (prop) {
        case 'insertable':
        case 'updatable':
        case 'filterable':
        case 'required':
        case 'sortable':
        case 'alignment':
        case 'colorExpression':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value) ||
      item.props.viewAttribute ||
      item.props.icon.iconName ||
      '',
    category: 'Data Grid',
    icon: { iconName: 'icons' },
    description: `**Overview**<br/>
The Icon Column renders a single, actionable icon in every row of a Data Grid.
It is typically used for specific, frequent actions like "View Details," "Download," "Favorite," or "Open Comments." Unlike the Action Icons Column (which acts as a generic container for multiple icons), this is a dedicated column type that can optionally bind to data to control the icon displayed.

**1. Configuration**<br/>
You can configure the appearance and behavior of the icon.
* **Icon:** Select the visual symbol (e.g., Trash, Eye, Star) to display.
* **Color:** Sets the icon's color (e.g., primary, #FF0000).
* **Size:** Controls the dimensions of the icon button (Small, Medium, Large).
* **Show Only On Hover:** If enabled, the icon remains invisible until the user hovers over the row, keeping the interface clean.

**2. Interaction & Logic**<br/>
The primary purpose of this column is to trigger actions.
* **TriggerOnClick:** Add this child component to execute a workflow when the icon is clicked. The trigger automatically receives the data of the specific row.
* **Menu:** Add a Menu component as a child to open a dropdown menu (e.g., "More Options") when the icon is clicked.
* **Comments Integration:**
    * **Comments Item ID:** If you have a Comments (Drawer) component on the page, select its Item ID here. Clicking the icon will automatically open the comments drawer for that specific record.

**3. Data Binding (Optional)**<br/>
While often used for static actions, this column can be data-driven.
* **View Attribute:** Select a String attribute from the Data Store.
* **Usage:** If bound, the value of this attribute can be used to dynamically determine the icon name (e.g., if the attribute value is "PDF", show a PDF icon; if "Image", show an Image icon).

**4. Restrictions**<br/>
* **Disable Condition:** A boolean expression (e.g., \`$.status === 'Closed'\`). If true, the icon is greyed out and unclickable for that specific row.
* **No Sorting/Filtering:** Standard sort/filter capabilities are disabled for this column type.`,
    helpRenders,
  };
};

export default IconColumnManager;
